import { useMemo, useState } from "react";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { toast } from "../lib/toast";
import * as XLSX from "xlsx";
import CostSummaryCard from "../components/layout/CostSummaryCard";
import { useExcelData } from "../../context/ExcelDataContext";
import { normalizeCostModelResult } from "../lib/normalizeCostModelResult";
import { saveCostModelAnalysis } from "../../services/ExcelDataContext";
import { CostBreakdownStructureCard } from "../components/dashboard/CostBreakdownStructureCard";
import { SensitivityTornadoCard } from "../components/dashboard/SensitivityTornadoCard";
import { ScenarioAnalysisCard } from "../components/dashboard/ScenarioAnalysisCard";
import { FiveYearForecastCard } from "../components/dashboard/FiveYearForecastCard";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell
} from "recharts";

const COST_MODEL_STANDALONE_STORAGE_KEY = "spendiq_cost_model_result_standalone";
const COST_MODEL_STANDALONE_RUNS_KEY = "spendiq_cost_model_runs_standalone";
const COST_MODEL_STANDALONE_DRAFT_KEY = "spendiq_cost_model_draft_standalone";
const COST_MODEL_STANDALONE_OPEN_PANEL_KEY = "spendiq_cost_model_open_standalone_panel";

type StandaloneRunRecord = {
    id: string;
    traceId: string;
    createdAt: string;
    product: string;
    country: string;
    currency: string;
    quantity: number | "";
    result: Record<string, any>;
    cId?: string;
    mId?: string;
};

type CostComponentCategory = "material" | "labour" | "capital" | "utility";

type CostComponentRow = {
    name: string;
    value: number;
    salary?: number;
    benefits?: number;
};

type ComparisonCountry = "The US" | "The UK" | "China" | "India" | "Spain" | "Japan" | "Germany";

const COMPARISON_COUNTRIES: ComparisonCountry[] = ["The US", "The UK", "China", "India", "Spain", "Japan", "Germany"];

const COMPARISON_COUNTRY_TO_CURRENCY: Record<ComparisonCountry, string> = {
    "The US": "USD",
    "The UK": "GBP",
    "China": "CNY",
    "India": "INR",
    "Spain": "EUR",
    "Japan": "JPY",
    "Germany": "EUR",
};

const FX_PER_USD: Record<string, number> = {
    USD: 1,
    GBP: 0.79,
    CNY: 7.18,
    INR: 83.2,
    EUR: 0.92,
    JPY: 156,
};

const COUNTRY_ALIASES: Record<string, ComparisonCountry> = {
    "the us": "The US",
    "us": "The US",
    "usa": "The US",
    "united states": "The US",
    "the uk": "The UK",
    "uk": "The UK",
    "united kingdom": "The UK",
    "china": "China",
    "india": "India",
    "spain": "Spain",
    "japan": "Japan",
    "germany": "Germany",
};

const CBS_COLORS = ["#3265AA", "#3265AA", "#AD5E9D", "#93c5fd", "#a78bfa", "#f9a8d4"];
const toNumber = (value: any): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};
const sumByKey = (rows: any[] | undefined, key: string): number =>
    (rows ?? []).reduce((acc, row) => acc + toNumber(row?.[key]), 0);
const getFieldTotal = (item: any, field: string): number => {
    const answer = item?.answer ?? {};
    const totals = answer?.totals ?? item?.totals ?? {};
    const totalValue = toNumber(totals?.[field]);

    if (totalValue > 0) return totalValue;
    if (field === "material_cost") return sumByKey(answer?.material_cost, "cost");
    if (field === "labour_cost") return sumByKey(answer?.labour_cost, "salary") + sumByKey(answer?.labour_cost, "benefits");
    if (field === "capital_cost") return sumByKey(answer?.capital_cost, "cost");
    if (field === "utility_cost") return sumByKey(answer?.utility_cost, "cost");

    return totalValue;
};
const sumField = (items: any[], field: string): number =>
    items.reduce((acc, item) => acc + getFieldTotal(item, field), 0);

const toPercent = (value: number, total: number): number =>
    Number(((value / (total || 1)) * 100).toFixed(2));

const formatCurrencyAmount = (value: number, currency: string): string => {
    const normalizedCurrency = (currency || "$" ).trim();
    const amount = `${value.toFixed(1)}`;

    // If backend returns an ISO code, keep a readable amount without forcing a symbol map.
    if (/^[A-Z]{3}$/.test(normalizedCurrency)) {
        return `${normalizedCurrency} ${amount}`;
    }

    return `${normalizedCurrency}${amount}`;
};

const formatCurrencyValue = (value: number, currency: string): string => {
    const normalizedCurrency = (currency || "$" ).trim();
    const amount = value.toLocaleString();

    if (/^[A-Z]{3}$/.test(normalizedCurrency)) {
        return `${normalizedCurrency} ${amount}`;
    }

    return `${normalizedCurrency}${amount}`;
};

const resolveComparisonCountry = (raw?: string): ComparisonCountry | null => {
    if (!raw) return null;
    const normalized = raw.toLowerCase().replace(/\./g, "").trim();
    return COUNTRY_ALIASES[normalized] ?? null;
};

const convertAmountByCurrency = (amount: number, fromCurrency: string, toCurrency: string): number => {
    const fromRate = FX_PER_USD[fromCurrency] ?? 1;
    const toRate = FX_PER_USD[toCurrency] ?? 1;
    return (amount / fromRate) * toRate;
};

type SectionCardProps = {
  title?: string;
  subtitle?: string;
  badge?: string;
  badgeTone?: "purple" | "mid" | "red";
  children: React.ReactNode;
};
function NoRecordsFound({ message = "No Records Found" }: { message?: string }) {
  return (
    <div className="cma-no-records" role="status" aria-live="polite">
      <svg width="120" height="100" viewBox="0 0 224 209" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M194.912 51.0825V59.2599" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M190.832 55.1617H198.99" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M27.6445 19.5302V27.6886" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M23.5488 23.6094H31.7263" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M91.1289 4.21874C92.1558 4.21874 92.9883 3.38627 92.9883 2.35937C92.9883 1.33247 92.1558 0.5 91.1289 0.5C90.102 0.5 89.2695 1.33247 89.2695 2.35937C89.2695 3.38627 90.102 4.21874 91.1289 4.21874Z" fill="#CFCFCF"></path><path d="M150.744 78.1381C134.863 78.1381 118.698 77.493 103.577 73.2809C88.7397 69.1638 75.117 61.1761 62.9742 51.9362C55.0244 45.9217 47.7957 41.1404 37.4743 41.8614C27.3682 42.4147 17.7088 46.2066 9.92525 52.6761C-3.20417 64.06 -1.23096 85.3668 4.0246 100.337C11.9174 122.877 35.9374 138.492 56.3525 148.699C79.9362 160.482 105.853 167.312 131.828 171.258C154.596 174.711 183.852 177.235 203.584 162.36C221.704 148.699 226.674 117.507 222.235 96.4472C221.159 90.22 217.846 84.5999 212.919 80.6425C200.188 71.3457 181.196 77.5499 166.89 77.8724C161.578 77.9863 156.17 78.1191 150.744 78.1381Z" fill="#F2F2F2"></path><path d="M111.889 208.161C150.356 208.161 181.539 206.216 181.539 203.816C181.539 201.417 150.356 199.471 111.889 199.471C73.4218 199.471 42.2383 201.417 42.2383 203.816C42.2383 206.216 73.4218 208.161 111.889 208.161Z" fill="#F2F2F2"></path><path d="M141.469 6.8014L48.7368 19.3392C45.7669 19.7408 43.6849 22.4738 44.0864 25.4437L62.0695 158.45C62.4711 161.419 65.2041 163.501 68.174 163.1L160.906 150.562C163.876 150.16 165.958 147.427 165.556 144.458L147.573 11.4517C147.171 8.48188 144.438 6.39986 141.469 6.8014Z" fill="#D2D2D2"></path><path d="M172.279 26.3224H78.7037C75.7068 26.3224 73.2773 28.7518 73.2773 31.7487V165.965C73.2773 168.962 75.7068 171.391 78.7037 171.391H172.279C175.276 171.391 177.706 168.962 177.706 165.965V31.7487C177.706 28.7518 175.276 26.3224 172.279 26.3224Z" fill="white" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M147.972 130.523H102.986C101.299 130.523 99.9316 131.891 99.9316 133.578V133.597C99.9316 135.284 101.299 136.651 102.986 136.651H147.972C149.659 136.651 151.026 135.284 151.026 133.597V133.578C151.026 131.891 149.659 130.523 147.972 130.523Z" fill="#D2D2D2"></path><path d="M146.359 155.568H103.195C102.682 155.568 102.266 155.984 102.266 156.497V156.782C102.266 157.295 102.682 157.711 103.195 157.711H146.359C146.873 157.711 147.289 157.295 147.289 156.782V156.497C147.289 155.984 146.873 155.568 146.359 155.568Z" fill="#D2D2D2"></path><path d="M157.365 145.531H93.786C93.1049 145.531 92.5527 146.083 92.5527 146.764C92.5527 147.445 93.1049 147.997 93.786 147.997H157.365C158.046 147.997 158.598 147.445 158.598 146.764C158.598 146.083 158.046 145.531 157.365 145.531Z" fill="#D2D2D2"></path><path d="M168.995 30.743H81.9846C79.826 30.743 78.0762 32.4929 78.0762 34.6515V110.108C78.0762 112.266 79.826 114.016 81.9846 114.016H168.995C171.154 114.016 172.904 112.266 172.904 110.108V34.6515C172.904 32.4929 171.154 30.743 168.995 30.743Z" fill="white" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M137.103 90.6225C137.723 90.6077 138.323 90.4047 138.825 90.0405C139.327 89.6762 139.706 89.168 139.912 88.5832C140.118 87.9984 140.141 87.3648 139.978 86.7666C139.815 86.1684 139.474 85.6339 139 85.2341C135.043 81.9561 130.054 80.183 124.915 80.2281C119.777 80.2733 114.819 82.1338 110.92 85.4807C110.45 85.8791 110.113 86.4111 109.952 87.0058C109.792 87.6006 109.816 88.2302 110.022 88.8108C110.228 89.3914 110.606 89.8956 111.105 90.2565C111.604 90.6174 112.202 90.8178 112.817 90.8312L137.103 90.6225Z" fill="white" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M152.604 71.6304L141.543 65.2554L152.604 58.8615" stroke="#BABABA" stroke-width="0.71" stroke-linecap="round" stroke-linejoin="round"></path><path d="M97.4668 71.6304L108.528 65.2554L97.4668 58.8615" stroke="#BABABA" stroke-width="0.71" stroke-linecap="round" stroke-linejoin="round"></path><path d="M36.8086 112.1V120.277" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M32.7324 116.179H40.9099" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M191.461 117.887C192.488 117.887 193.32 117.054 193.32 116.028C193.32 115.001 192.488 114.168 191.461 114.168C190.434 114.168 189.602 115.001 189.602 116.028C189.602 117.054 190.434 117.887 191.461 117.887Z" fill="#CFCFCF"></path></svg>
      {message}
    </div>
  );
}
function SectionCard({ title, subtitle, badge, badgeTone = "purple", children }: SectionCardProps) {
  return (
    <div className="cma-card">
      <div className="cma-card-header">
        <div>
          <div className="cma-card-title">{title}</div>
          {subtitle && <div className="cma-card-subtitle">{subtitle}</div>}
        </div>
        {badge && <span className={`cma-card-badge cma-card-badge-${badgeTone}`}>{badge}</span>}
      </div>
      {children}
    </div>
  );
}

export default function CostModelAnalysisStandalone() {
    const [isStandaloneMode, setIsStandaloneMode] = useState(true);
    const [selectedCostComponentCategory, setSelectedCostComponentCategory] = useState<CostComponentCategory>("material");
    const [selectedComparisonCountryA, setSelectedComparisonCountryA] = useState<ComparisonCountry | "">("");
    const [selectedComparisonCountryB, setSelectedComparisonCountryB] = useState<ComparisonCountry | "">("");
    const [selectedComparisonCountryC, setSelectedComparisonCountryC] = useState<ComparisonCountry | "">("");
    const { analysisResult, navigate, standaloneAnalysisResult: sharedStandaloneResult } = useExcelData();
    const standaloneDraft = useMemo(() => {
        try {
            const rawDraft = localStorage.getItem(COST_MODEL_STANDALONE_DRAFT_KEY);
            if (!rawDraft) return null;

            const parsedDraft = JSON.parse(rawDraft);
            return parsedDraft && typeof parsedDraft === "object" ? parsedDraft : null;
        } catch {
            return null;
        }
    }, []);

    const standaloneAnalysisResult = useMemo(() => {
        if (sharedStandaloneResult) return normalizeCostModelResult(sharedStandaloneResult);

        try {
            const rawRuns = localStorage.getItem(COST_MODEL_STANDALONE_RUNS_KEY);
            const parsedRuns = rawRuns ? JSON.parse(rawRuns) : [];
            const runs = Array.isArray(parsedRuns) ? parsedRuns : [];
            if (runs.length > 0 && runs[0]?.result) return normalizeCostModelResult(runs[0].result);

            return normalizeCostModelResult(analysisResult);
        } catch {
            return normalizeCostModelResult(analysisResult);
        }
    }, [analysisResult, sharedStandaloneResult]);

    const selectedStandaloneRun = useMemo(() => {
        try {
            if (standaloneDraft) return standaloneDraft;

            const rawRuns = localStorage.getItem(COST_MODEL_STANDALONE_RUNS_KEY);
            const parsedRuns = rawRuns ? JSON.parse(rawRuns) : [];
            const runs = Array.isArray(parsedRuns) ? parsedRuns : [];
            if (runs.length === 0) return standaloneDraft;

            const traceId = standaloneAnalysisResult?.trace_id;
            if (!traceId) return runs[0];

            const matched = runs.find(
                (run: any) =>
                    run?.traceId === traceId ||
                    run?.result?.trace_id === traceId ||
                    run?.result?.headers?.trace_id === traceId
            );
            return matched || runs[0] || standaloneDraft;
        } catch {
            return standaloneDraft;
        }
    }, [standaloneAnalysisResult, standaloneDraft]);

    const handleExport = () => {
        try {
            const result = standaloneAnalysisResult;

            if (!result) {
                toast.error("No data available to export.");
                return;
            }

            const foreachItems: any[] = result?.data?.output?.foreach ?? [];
            const workbook = XLSX.utils.book_new();

            // ── Sheet 1: Summary ──────────────────────────────────────────────
            const summaryRows: Record<string, any>[] = foreachItems.map((item) => {
                const answer = item?.answer ?? {};
                const totals = answer?.totals ?? {};
                const heading = answer?.heading ?? {};
                return {
                    "Product": heading?.line_item_description ?? "",
                    "Region": heading?.supplier_region ?? "",
                    "Currency": totals?.currency ?? "",
                    "Material Cost": totals?.material_cost ?? 0,
                    "Labour Cost": totals?.labour_cost ?? 0,
                    "Capital Cost": totals?.capital_cost ?? 0,
                    "Utility Cost": totals?.utility_cost ?? 0,
                    "Overheads": totals?.overheads ?? 0,
                    "Margins": totals?.margins ?? 0,
                    "Total Production Cost": totals?.total_production_cost ?? 0,
                    "Total Cost": totals?.total_cost ?? 0,
                };
            });

            if (summaryRows.length > 0) {
                XLSX.utils.book_append_sheet(
                    workbook,
                    XLSX.utils.json_to_sheet(summaryRows),
                    "Summary"
                );
            }

            // ── Sheet 2: Material Cost ─────────────────────────────────────
            const materialRows: Record<string, any>[] = foreachItems.flatMap((item) => {
                const product = item?.answer?.heading?.line_item_description ?? "";
                return (item?.answer?.material_cost ?? []).map((entry: any) => ({
                    "Product": product,
                    "Material Name": entry?.material_name ?? "",
                    "Cost": entry?.cost ?? 0,
                }));
            });

            if (materialRows.length > 0) {
                XLSX.utils.book_append_sheet(
                    workbook,
                    XLSX.utils.json_to_sheet(materialRows),
                    "Material Cost"
                );
            }

            // ── Sheet 3: Labour Cost ───────────────────────────────────────
            const labourRows: Record<string, any>[] = foreachItems.flatMap((item) => {
                const product = item?.answer?.heading?.line_item_description ?? "";
                return (item?.answer?.labour_cost ?? []).map((entry: any) => ({
                    "Product": product,
                    "Designation / Type": entry?.designation ?? entry?.type ?? "",
                    "Salary": entry?.salary ?? 0,
                    "Benefits": entry?.benefits ?? entry?.benefits_cost ?? 0,
                }));
            });

            if (labourRows.length > 0) {
                XLSX.utils.book_append_sheet(
                    workbook,
                    XLSX.utils.json_to_sheet(labourRows),
                    "Labour Cost"
                );
            }

            // ── Sheet 4: Capital Cost ──────────────────────────────────────
            const capitalRows: Record<string, any>[] = foreachItems.flatMap((item) => {
                const product = item?.answer?.heading?.line_item_description ?? "";
                return (item?.answer?.capital_cost ?? []).map((entry: any) => ({
                    "Product": product,
                    "Equipment Name": entry?.equipment_name ?? "",
                    "Cost": entry?.cost ?? 0,
                }));
            });

            if (capitalRows.length > 0) {
                XLSX.utils.book_append_sheet(
                    workbook,
                    XLSX.utils.json_to_sheet(capitalRows),
                    "Capital Cost"
                );
            }

            // ── Sheet 5: Utility / Electricity Cost ───────────────────────
            const utilityRows: Record<string, any>[] = foreachItems.flatMap((item) => {
                const product = item?.answer?.heading?.line_item_description ?? "";
                return (item?.answer?.utility_cost ?? []).map((entry: any) => ({
                    "Product": product,
                    "Equipment Name": entry?.equipment_name ?? "",
                    "Cost": entry?.cost ?? 0,
                }));
            });

            if (utilityRows.length > 0) {
                XLSX.utils.book_append_sheet(
                    workbook,
                    XLSX.utils.json_to_sheet(utilityRows),
                    "Electricity Cost"
                );
            }

            const productLabel = (standaloneDraft?.product || selectedStandaloneRun?.product || "cost-model")
                .replace(/[^a-zA-Z0-9_-]/g, "_")
                .slice(0, 40);
            const timestamp = new Date().toISOString().slice(0, 10);
            const fileName = `${productLabel}_${timestamp}.xlsx`;

            XLSX.writeFile(workbook, fileName);
            toast.success("Exported successfully.");
        } catch {
            toast.error("Export failed. Please try again.");
        }
    };

    const handleSaveVersion = async () => {
        const latestResult = standaloneAnalysisResult;
        if (!latestResult) {
            toast.error("No standalone result found to save.");
            return;
        }

        const traceId = String(latestResult?.trace_id || standaloneDraft?.traceId || selectedStandaloneRun?.traceId || "");
        const cId = standaloneDraft?.cId || selectedStandaloneRun?.cId || "";
        const mId = standaloneDraft?.mId || selectedStandaloneRun?.mId || "";
        const runId = `${traceId || "no-trace"}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const runRecord: StandaloneRunRecord = {
            id: runId,
            traceId,
            createdAt: new Date().toISOString(),
            product: standaloneDraft?.product || selectedStandaloneRun?.product || "Standalone Product",
            country: standaloneDraft?.country || selectedStandaloneRun?.country || "Standalone Region",
            currency: standaloneDraft?.currency || selectedStandaloneRun?.currency || "",
            quantity: standaloneDraft?.quantity ?? selectedStandaloneRun?.quantity ?? "",
            result: latestResult,
            cId,
            mId,
        };

        try {
            const raw = localStorage.getItem(COST_MODEL_STANDALONE_RUNS_KEY);
            const existingRuns = raw ? JSON.parse(raw) : [];
            const safeRuns = Array.isArray(existingRuns) ? existingRuns : [];
            const updatedRuns = [runRecord, ...safeRuns];
            localStorage.setItem(COST_MODEL_STANDALONE_RUNS_KEY, JSON.stringify(updatedRuns));
            localStorage.removeItem(COST_MODEL_STANDALONE_DRAFT_KEY);
            localStorage.setItem(COST_MODEL_STANDALONE_OPEN_PANEL_KEY, "1");

            // Await the insert so the saved-models list already has this row when the view remounts.
            await saveCostModelAnalysis({
                request_id: traceId,
                input_data: { cId, mId },
                output_data: latestResult,
                is_standalone: true,
            });

            toast.success("Version saved successfully.");
            navigate("on-demand-cost-analysis");
        } catch (err) {
            console.error("Failed to persist standalone cost model analysis:", err);
            toast.error("Unable to save version.");
        }
    };

    const scopedItems: any[] = standaloneAnalysisResult?.data?.output?.foreach ?? [];
    const summaryCurrency = useMemo(() => {
        for (const item of scopedItems) {
            const currency = item?.answer?.totals?.currency;
            if (typeof currency === "string" && currency.trim() !== "") {
                return currency.trim();
            }
        }

        return "$";
    }, [scopedItems]);

    const summaryTotalCost = useMemo(() => {
        const total = sumField(scopedItems, "total_cost");
        return formatCurrencyAmount(total, summaryCurrency);
    }, [scopedItems, summaryCurrency]);

    const summaryProduct = standaloneDraft?.product || selectedStandaloneRun?.product || "Standalone Product";
    const summaryRegion = standaloneDraft?.country || selectedStandaloneRun?.country || "Standalone Region";
    const summaryQty = (standaloneDraft?.quantity ?? selectedStandaloneRun?.quantity) === "" || (standaloneDraft?.quantity ?? selectedStandaloneRun?.quantity) === undefined
        ? "Per unit"
        : `Qty ${standaloneDraft?.quantity ?? selectedStandaloneRun?.quantity}`;
    const summaryYear = (standaloneDraft?.createdAt || selectedStandaloneRun?.createdAt)
        ? new Date(standaloneDraft?.createdAt || selectedStandaloneRun?.createdAt).getFullYear().toString()
        : new Date().getFullYear().toString();

    const baseTotalCost = useMemo(() => sumField(scopedItems, "total_cost"), [scopedItems]);

    const baseComparisonCountry = useMemo<ComparisonCountry>(() => {
        const fromRegion = resolveComparisonCountry(summaryRegion);
        if (fromRegion) return fromRegion;

        const fromDraftRegion = resolveComparisonCountry(standaloneDraft?.country);
        if (fromDraftRegion) return fromDraftRegion;

        const fromRunRegion = resolveComparisonCountry(selectedStandaloneRun?.country);
        if (fromRunRegion) return fromRunRegion;

        return "India";
    }, [summaryRegion, standaloneDraft, selectedStandaloneRun]);

    const baseCurrencyCode = useMemo(() => {
        const fromCountry = COMPARISON_COUNTRY_TO_CURRENCY[baseComparisonCountry];
        if (fromCountry) return fromCountry;

        if (/^[A-Z]{3}$/.test(summaryCurrency)) return summaryCurrency;

        const fallbackFromRun = String(standaloneDraft?.currency || selectedStandaloneRun?.currency || "").trim();
        if (/^[A-Z]{3}$/.test(fallbackFromRun)) return fallbackFromRun;

        return "USD";
    }, [baseComparisonCountry, summaryCurrency, standaloneDraft, selectedStandaloneRun]);

    const comparisonFilterOptions = useMemo(
        () => COMPARISON_COUNTRIES.filter((country) => country !== baseComparisonCountry),
        [baseComparisonCountry]
    );

    const selectedComparisonCountries = useMemo(() => {
        const pick = (preferred: ComparisonCountry | "", used: Set<ComparisonCountry>): ComparisonCountry | null => {
            if (preferred && comparisonFilterOptions.includes(preferred) && !used.has(preferred)) {
                used.add(preferred);
                return preferred;
            }

            const fallback = comparisonFilterOptions.find((country) => !used.has(country));
            if (!fallback) return null;
            used.add(fallback);
            return fallback;
        };

        const used = new Set<ComparisonCountry>();
        const first = pick(selectedComparisonCountryA, used);
        const second = pick(selectedComparisonCountryB, used);
        const third = pick(selectedComparisonCountryC, used);

        return [first, second, third].filter((country): country is ComparisonCountry => Boolean(country));
    }, [comparisonFilterOptions, selectedComparisonCountryA, selectedComparisonCountryB, selectedComparisonCountryC]);
    const standaloneCbsData = useMemo(() => {
    if (scopedItems.length === 0) return [];

    const totals = {
            material_cost: sumField(scopedItems, "material_cost"),
            labour_cost: sumField(scopedItems, "labour_cost"),
            capital_cost: sumField(scopedItems, "capital_cost"),
            utility_cost: sumField(scopedItems, "utility_cost"),
            overheads: sumField(scopedItems, "overheads"),
            margins: sumField(scopedItems, "margins"),
            total_cost: sumField(scopedItems, "total_cost"),
    };

    const componentTotal = totals.material_cost + totals.labour_cost + totals.capital_cost + totals.utility_cost + totals.overheads + totals.margins;
    const totalCost = totals.total_cost || componentTotal || 1;
    return [
            { name: "Material Cost", value: toPercent(totals.material_cost, totalCost), color: CBS_COLORS[0] },
            { name: "Labour Cost", value: toPercent(totals.labour_cost, totalCost), color: CBS_COLORS[1] },
            { name: "Capital Cost", value: toPercent(totals.capital_cost, totalCost), color: CBS_COLORS[2] },
            { name: "Utility Cost", value: toPercent(totals.utility_cost, totalCost), color: CBS_COLORS[3] },
            { name: "Overheads", value: toPercent(totals.overheads, totalCost), color: CBS_COLORS[4] },
            { name: "Margins", value: toPercent(totals.margins, totalCost), color: CBS_COLORS[5] },
    ];
    }, [scopedItems]);

    const standaloneTornadoItems = useMemo(() => {
        if (scopedItems.length === 0) return [];

        const costDrivers = [
            { label: "Material Cost", value: sumField(scopedItems, "material_cost") },
            { label: "Labour Cost", value: sumField(scopedItems, "labour_cost") },
            { label: "Capital Cost", value: sumField(scopedItems, "capital_cost") },
            { label: "Utility Cost", value: sumField(scopedItems, "utility_cost") },
        ];

        return costDrivers.map((driver) => ({
            label: driver.label,
            neg: -parseFloat((driver.value * 0.9).toFixed(1)),
            pos: parseFloat((driver.value * 1.1).toFixed(1)),
        }));
    }, [scopedItems]);

    const standaloneScenarioData = useMemo(() => {
        const totalCost = sumField(scopedItems, "total_cost");
        const cagr = 1.07;
        const yearsToForecast = 2;

        return [
            { name: "2025 Pessimistic", value: Math.round(totalCost * 1.1) },
            { name: "2025 Base", value: Math.round(totalCost) },
            { name: "2025 Optimistic", value: Math.round(totalCost * 0.95) },
            { name: "2027 Pessimistic", value: Math.round((totalCost * 1.1) * Math.pow(cagr, yearsToForecast)) },
            { name: "2027 Base", value: Math.round(totalCost * Math.pow(cagr, yearsToForecast)) },
            { name: "2027 Optimistic", value: Math.round((totalCost * 0.95) * Math.pow(cagr, yearsToForecast)) },
        ];
    }, [scopedItems]);

    const standaloneForecastData = useMemo(() => {
        const baseCost = sumField(scopedItems, "total_cost");
        const yearInflation = [
            { label: "2025 E", rate: 1.039 },
            { label: "2026 F", rate: 1.04 },
            { label: "2027 F", rate: 1.042 },
            { label: "2028 F", rate: 1.048 },
            { label: "2029 F", rate: 1.044 },
            { label: "2030 F", rate: 1.041 },
        ];

        let prev = baseCost;
        return yearInflation.map(({ label, rate }, i) => {
            const base = Math.round(prev * rate);
            prev = base;
            const savingsFactor = i === 0 ? 1 : 1 - i * 0.026;
            const savings = Math.round(base * savingsFactor);
            return { year: label, base, savings };
        });
    }, [scopedItems]);
    
    const costBreakdownData = useMemo(() => {
        if (scopedItems.length === 0) return [];

        const totals = {
            material_cost: sumField(scopedItems, "material_cost"),
            labour_cost: sumField(scopedItems, "labour_cost"),
            capital_cost: sumField(scopedItems, "capital_cost"),
            utility_cost: sumField(scopedItems, "utility_cost"),
            overheads: sumField(scopedItems, "overheads"),
            margins: sumField(scopedItems, "margins"),
            total_cost: sumField(scopedItems, "total_cost"),
        };

        const toLabel = (value: number) => formatCurrencyAmount(value, summaryCurrency);

        return [
            { name: "Material Cost", value: totals.material_cost, label: toLabel(totals.material_cost) },
            { name: "Labor Cost", value: totals.labour_cost, label: toLabel(totals.labour_cost) },
            { name: "Capital Cost", value: totals.capital_cost, label: toLabel(totals.capital_cost) },
            { name: "Utility Cost", value: totals.utility_cost, label: toLabel(totals.utility_cost) },
            { name: "Overheads", value: totals.overheads, label: toLabel(totals.overheads) },
            { name: "Margins", value: totals.margins, label: toLabel(totals.margins) },
        ];
    }, [scopedItems, summaryCurrency]);

    const costBreakdownAxis = useMemo(() => {
        const maxValue = Math.max(0, ...costBreakdownData.map((item) => Number(item.value) || 0));
        if (maxValue <= 0) {
            return { max: 100, ticks: [0, 25, 50, 75, 100] };
        }

        const paddedMax = maxValue * 1.1;
        const roughStep = paddedMax / 4;
        const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
        const normalized = roughStep / magnitude;

        let step = 10 * magnitude;
        if (normalized <= 1) step = 1 * magnitude;
        else if (normalized <= 2) step = 2 * magnitude;
        else if (normalized <= 5) step = 5 * magnitude;

        const max = Math.ceil(paddedMax / step) * step;
        const ticks = Array.from({ length: Math.floor(max / step) + 1 }, (_, i) => i * step);
        return { max, ticks };
    }, [costBreakdownData]);
    
    const CustomBarLabel = (props: any) => {
        const { x, y, width, payload } = props;
        if (!payload || !payload.label) return null;
        return (
            <text
            x={x + width / 2}
            y={y - 10}
            fill="#EA580C"
            textAnchor="middle"
            fontSize={16}
            fontWeight={700}
            >
            {payload.label}
            </text>
        );
    };

    const costComponentData = useMemo<CostComponentRow[]>(() => {
        if (scopedItems.length === 0) return [];

        const rows = scopedItems.flatMap((item) => item?.answer ? [item.answer] : []);

        if (selectedCostComponentCategory === "material") {
            return rows.flatMap((answer: any) =>
                (answer?.material_cost ?? []).map((entry: any) => ({
                    name: String(entry?.material_name ?? "Material"),
                    value: toNumber(entry?.cost),
                }))
            );
        }

        if (selectedCostComponentCategory === "labour") {
            const labourEntries = rows.flatMap((answer: any) => answer?.labour_cost ?? []);
            const designatedRows = labourEntries.filter((entry: any) => entry?.designation);

            const totalSalary = designatedRows.reduce((acc: number, entry: any) => acc + toNumber(entry?.salary), 0);
            const benefitsEntries = labourEntries.filter((entry: any) => entry?.type === "benefits");
            const benefitsCostTotal = benefitsEntries.reduce((acc: number, entry: any) => acc + toNumber(entry?.benefits_cost), 0);
            const baseSalaryFromPayload = benefitsEntries.reduce((acc: number, entry: any) => acc + toNumber(entry?.base_salary), 0);
            const baseSalary = baseSalaryFromPayload > 0 ? baseSalaryFromPayload : totalSalary;

            return designatedRows.map((entry: any) => {
                const salary = toNumber(entry?.salary);
                const allocatedBenefits = baseSalary > 0
                    ? Number((benefitsCostTotal * (salary / baseSalary)).toFixed(2))
                    : 0;

                return {
                    name: String(entry?.designation ?? "Labour"),
                    value: Number((salary + allocatedBenefits).toFixed(2)),
                    salary,
                    benefits: allocatedBenefits,
                };
            });
        }

        if (selectedCostComponentCategory === "capital") {
            return rows.flatMap((answer: any) =>
                (answer?.capital_cost ?? []).map((entry: any) => ({
                    name: String(entry?.equipment_name ?? "Equipment"),
                    value: toNumber(entry?.cost),
                }))
            );
        }

        return rows.flatMap((answer: any) =>
            (answer?.utility_cost ?? []).map((entry: any) => ({
                name: String(entry?.equipment_name ?? "Utility"),
                value: toNumber(entry?.cost),
            }))
        );
    }, [scopedItems, selectedCostComponentCategory]);

    const costComponentAxis = useMemo(() => {
        const maxValue = Math.max(0, ...costComponentData.map((item) => Number(item.value) || 0));
        if (maxValue <= 0) {
            return { max: 100, ticks: [0, 25, 50, 75, 100] };
        }

        const paddedMax = maxValue * 1.1;
        const roughStep = paddedMax / 4;
        const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
        const normalized = roughStep / magnitude;

        let step = 10 * magnitude;
        if (normalized <= 1) step = 1 * magnitude;
        else if (normalized <= 2) step = 2 * magnitude;
        else if (normalized <= 5) step = 5 * magnitude;

        const max = Math.ceil(paddedMax / step) * step;
        const ticks = Array.from({ length: Math.floor(max / step) + 1 }, (_, i) => i * step);
        return { max, ticks };
    }, [costComponentData]);

    const costComparisonData = useMemo(() => {
        const bars: Array<{ country: string; cost: number; color: string; currency: string }> = [];

        bars.push({
            country: baseComparisonCountry,
            cost: Number(baseTotalCost.toFixed(2)),
            color: "#4B235F",
            currency: baseCurrencyCode,
        });

        for (const country of selectedComparisonCountries) {
            const targetCurrency = COMPARISON_COUNTRY_TO_CURRENCY[country];
            const convertedCost = convertAmountByCurrency(baseTotalCost, baseCurrencyCode, targetCurrency);
            bars.push({
                country,
                cost: Number(convertedCost.toFixed(2)),
                color: "#0F758F",
                currency: targetCurrency,
            });
        }

        return bars;
    }, [baseComparisonCountry, baseCurrencyCode, baseTotalCost, selectedComparisonCountries]);

    const ActionToolbar = () => {
    return (
        <div
        style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "16px 20px",
            borderBottom: "1px solid #E5E7EB",
        }}
        >
        <button
            type="button"
            onClick={handleSaveVersion}
            style={{
            background: "#4B235F",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "6px",
            padding: "9px 14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "13px",
            fontWeight: 500,
            cursor: "pointer",
            }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.86667 1.75C9.1744 1.75438 9.46793 1.88018 9.68333 2.1L11.9 4.31667C12.1198 4.53207 12.2456 4.8256 12.25 5.13333V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V2.91667C1.75 2.60725 1.87292 2.3105 2.09171 2.09171C2.3105 1.87292 2.60725 1.75 2.91667 1.75H8.86667Z" stroke="white" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9.91683 12.2487V8.16536C9.91683 8.01065 9.85537 7.86228 9.74598 7.75289C9.63658 7.64349 9.48821 7.58203 9.3335 7.58203H4.66683C4.51212 7.58203 4.36375 7.64349 4.25435 7.75289C4.14495 7.86228 4.0835 8.01065 4.0835 8.16536V12.2487" stroke="white" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M4.0835 1.75V4.08333C4.0835 4.23804 4.14495 4.38642 4.25435 4.49581C4.36375 4.60521 4.51212 4.66667 4.66683 4.66667H8.75016" stroke="white" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Save version
        </button>

        <button className="toolbar-btn" type="button" onClick={handleExport}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.25 8.75V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V8.75" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M4.0835 5.83203L7.00016 8.7487L9.91683 5.83203" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M7 8.75V1.75" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>  
            Export
        </button>

        <button className="toolbar-btn" type="button" disabled>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.5 12.25C11.4665 12.25 12.25 11.4665 12.25 10.5C12.25 9.5335 11.4665 8.75 10.5 8.75C9.5335 8.75 8.75 9.5335 8.75 10.5C8.75 11.4665 9.5335 12.25 10.5 12.25Z" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M3.5 5.25C4.4665 5.25 5.25 4.4665 5.25 3.5C5.25 2.5335 4.4665 1.75 3.5 1.75C2.5335 1.75 1.75 2.5335 1.75 3.5C1.75 4.4665 2.5335 5.25 3.5 5.25Z" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M7.5835 3.5H9.3335C9.64292 3.5 9.93966 3.62292 10.1585 3.84171C10.3772 4.0605 10.5002 4.35725 10.5002 4.66667V8.75" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M6.41667 10.5H4.66667C4.35725 10.5 4.0605 10.3771 3.84171 10.1583C3.62292 9.9395 3.5 9.64275 3.5 9.33333V5.25" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Compare
        </button>

        <button className="toolbar-btn" type="button" disabled>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.2498 2.33203H8.1665" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M5.83333 2.33203H1.75" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12.25 7H7" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4.66667 7H1.75" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12.2502 11.668H9.3335" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M7 11.668H1.75" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8.1665 1.16797V3.5013" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4.6665 5.83203V8.16536" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9.3335 10.5V12.8333" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Refine inputs
        </button>

        <button className="toolbar-btn" type="button" disabled>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.98975 12.25C6.09215 12.4273 6.23942 12.5746 6.41677 12.677C6.59412 12.7794 6.7953 12.8333 7.00008 12.8333C7.20486 12.8333 7.40604 12.7794 7.58339 12.677C7.76074 12.5746 7.90801 12.4273 8.01041 12.25" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M1.9029 8.94147C1.82669 9.02499 1.7764 9.12886 1.75814 9.24044C1.73989 9.35202 1.75445 9.4665 1.80006 9.56995C1.84567 9.67341 1.92036 9.76138 2.01504 9.82316C2.10973 9.88495 2.22033 9.91789 2.3334 9.91797H11.6667C11.7798 9.91801 11.8904 9.8852 11.9852 9.82353C12.0799 9.76186 12.1547 9.67399 12.2004 9.5706C12.2461 9.46721 12.2608 9.35277 12.2427 9.24118C12.2246 9.12959 12.1745 9.02567 12.0984 8.94205C11.3226 8.1423 10.5001 7.29239 10.5001 4.66797C10.5001 3.73971 10.1313 2.84947 9.47494 2.19309C8.81856 1.53672 7.92832 1.16797 7.00006 1.16797C6.0718 1.16797 5.18157 1.53672 4.52519 2.19309C3.86881 2.84947 3.50006 3.73971 3.50006 4.66797C3.50006 7.29239 2.67698 8.1423 1.9029 8.94147Z" stroke="#333333" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Track
        </button>

        <button className="toolbar-btn" type="button" style={{ marginLeft: "auto" }} onClick={() => navigate("on-demand-cost-analysis")}>
            <ArrowLeft size={14} aria-hidden="true" />
            Back
        </button>
        </div>
    );
    };
    
    return (
        <>
            <div className="cma-view">
                {/* Page header */}
                <nav className="breadcrumb" aria-label="Breadcrumb">
                    <span className="breadcrumb__current">Cost Model Analysis</span>
                </nav>
                <div className="page-header flex justify-between" style={{ marginBottom: 28 }}>
                <div className="page-header__left">
                    <h2 className="page-header__title">Cost Model Analysis</h2>
                    <p className="page-header__description">
                    Well Construction Services · CBS, Should-Cost, Sensitivity & Forecasting
                    </p>
                </div>
                {/* <div className="page-header__right">
                    <div className="inner-bg flex">
                    <button
                        type="button"
                        className={`btn btn-primary ${isStandaloneMode ? "bg-white shadow radius6" : ""}`}
                        onClick={() => setIsStandaloneMode(true)}
                        aria-pressed={isStandaloneMode}
                    >
                        On-Demand Cost Analysis
                    </button>
                    <button
                        type="button"
                        className={`flex items-center ${!isStandaloneMode ? "bg-white shadow radius6" : ""}`}
                        onClick={() => {
                            setIsStandaloneMode(false);
                            navigate("cost-model-analysis");
                        }}
                        aria-pressed={!isStandaloneMode}>
                        <span>Project Selected</span>
                        <ChevronDown size={14} style={{ marginLeft: 8, color: "#000", transition: "color 0.15s, transform 0.18s" }} />
                    </button>
                    </div>
                </div> */}
                </div>
                <CostSummaryCard
                    product={summaryProduct}
                    timePeriod={summaryYear}
                    countryRegion={summaryRegion}
                    units={summaryQty}
                    totalCost={summaryTotalCost}
                />
            </div>
            {/* Main Page */}
            <div className="cma-content">
                <div className="cma-two-col">
                    <CostBreakdownStructureCard
                        data={standaloneCbsData}
                        isShowTags={false}
                    />
                    <SectionCard
                        title={`Cost Breakdown by Components (${summaryCurrency})`}
                        subtitle=""
                        badge="">
                        {costBreakdownData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={350}>
                            <BarChart
                                data={costBreakdownData}
                                margin={{ top: 40, right: 20, left: 10, bottom: 50 }}>
                                <XAxis
                                dataKey="name"
                                interval={0}
                                tick={{ fontSize: 10, fill: "#666" }}
                                tickFormatter={(value) =>
                                    typeof value === "string"
                                    ? value.split("\n")[0]
                                    : value
                                }/>
                                <YAxis
                                domain={[0, costBreakdownAxis.max]}
                                ticks={costBreakdownAxis.ticks}
                                tickFormatter={(value) => value.toLocaleString()}
                                tick={{ fontSize: 12, fill: "#666" }}
                                />
                                <Tooltip
                                formatter={(value: number) => [formatCurrencyValue(value, summaryCurrency), "Value"]}
                                contentStyle={{borderRadius: 8,}}
                                cursor={false}
                                />
                                <Bar
                                dataKey="value"
                                fill="#F45D01"
                                radius={[4, 4, 0, 0]}
                                barSize={60}
                                label={<CustomBarLabel />}
                                />
                            </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <NoRecordsFound />
                        )}
                    </SectionCard>
                </div>
                <div className="cma-two-col">
                    <SectionCard title={`Cost Breakdown by Each Cost Components (${summaryCurrency})`}>
                        {costComponentData.length > 0 ? (
                            <>
                            <div style={{ display: "flex", gap: "12px", marginBottom: "20px", alignItems: "center" }}>
                                <select
                                value={selectedCostComponentCategory}
                                onChange={(event) => setSelectedCostComponentCategory(event.target.value as CostComponentCategory)}
                                style={{
                                    width: 220,
                                    height: 36,
                                    border: "1px solid #D9DEE8",
                                    borderRadius: 8,
                                    background: "#fff",
                                    padding: "0 10px",
                                    fontSize: 13,
                                    color: "#364153",
                                }}
                                aria-label="Select cost category"
                                >
                                    <option value="material">Material Cost</option>
                                    <option value="labour">Labour Cost</option>
                                    <option value="capital">Capital Cost</option>
                                    <option value="utility">Utility Cost</option>
                                </select>
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart
                                data={costComponentData}
                                margin={{
                                    top: 10,
                                    right: 10,
                                    left: 0,
                                    bottom: 0,
                                }}
                                >
                                <CartesianGrid
                                    strokeDasharray="4 4"
                                    vertical={false}
                                    stroke="#D9DEE8"
                                />
                                <XAxis
                                    dataKey="name"
                                    tick={{
                                    fontSize: 12,
                                    fill: "#666",
                                    }}
                                />
                                <YAxis
                                    domain={[0, costComponentAxis.max]}
                                    ticks={costComponentAxis.ticks}
                                    tickFormatter={(value) => value.toLocaleString()}
                                    tick={{
                                    fontSize: 12,
                                    fill: "#666",
                                    }}
                                />
                                <Tooltip
                                    cursor={false}
                                    formatter={(value: number, _name, props: any) => {
                                        if (selectedCostComponentCategory === "labour") {
                                            const row = props?.payload as CostComponentRow | undefined;
                                            if (row) {
                                                const salary = row.salary ?? value;
                                                const benefits = row.benefits ?? 0;
                                                return [
                                                    `${formatCurrencyValue(value, summaryCurrency)} (Salary: ${formatCurrencyValue(salary, summaryCurrency)}, Benefits: ${formatCurrencyValue(benefits, summaryCurrency)})`,
                                                    "Cost",
                                                ];
                                            }
                                        }
                                        return [
                                            formatCurrencyValue(value, summaryCurrency),
                                            selectedCostComponentCategory === "utility" ? "Electricity Cost" : "Cost",
                                        ];
                                    }}
                                />
                                <Bar
                                    dataKey="value"
                                    fill="#223F96"
                                    radius={[4, 4, 0, 0]}
                                    barSize={70}
                                    activeBar={false}
                                />
                                </BarChart>
                            </ResponsiveContainer>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                                <div style={{ width: 14, height: 14, borderRadius: 2, background: "#223F96" }} />
                                <span style={{ fontSize: 14 }}>
                                  {selectedCostComponentCategory === "material" && "Material Cost"}
                                  {selectedCostComponentCategory === "labour" && "Labour Cost (salary + allocated benefits)"}
                                  {selectedCostComponentCategory === "capital" && "Capital Cost"}
                                  {selectedCostComponentCategory === "utility" && "Utility Cost"}
                                </span>
                            </div>
                            </>
                        ) : (
                            <NoRecordsFound />
                        )}
                    </SectionCard>
                    <SensitivityTornadoCard
                        items={standaloneTornadoItems}
                        insightTitle="Key Risk Drivers"
                        insightText="This standalone analysis is scoped to your selected product and region inputs, so sensitivity reflects only that single-product scenario without global dashboard filters."
                        isShowTags={false}
                        emptyState={<NoRecordsFound />}
                    />
                </div>
                <div className="cma-two-col">
                    <ScenarioAnalysisCard
                        data={standaloneScenarioData}
                        colorA="#AD5E9D"
                        colorB="#3265AA"
                        colorC="#AD5E9D"
                        tickColor="#4c4c4d"
                        isShowTags={false}
                        emptyState={<NoRecordsFound />}
                    />
                    <FiveYearForecastCard
                        data={standaloneForecastData}
                        baseStroke="#3265AA"
                        savingsStroke="#AD5E9D"
                        tickColor="#4c4c4d"
                        mutedTickColor="#9e97a3"
                        isShowTags={false}
                        emptyState={<NoRecordsFound />}
                    />
                </div>
                <div className="cma-one-col">
                    <SectionCard title="Cost Comparison - Reporting Country vs Leading Country">
                    <>
                    <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
                    <div style={{
                        minWidth: 180,
                        height: 36,
                        border: "1px solid #D9DEE8",
                        borderRadius: 8,
                        background: "#fff",
                        display: "flex",
                        alignItems: "center",
                        padding: "0 12px",
                        fontSize: 13,
                        color: "#364153",
                    }}>
                        Base: {baseComparisonCountry}
                    </div>

                    <select
                        value={selectedComparisonCountryA}
                        onChange={(event) => setSelectedComparisonCountryA(event.target.value as ComparisonCountry)}
                        style={{ minWidth: 180, height: 36, border: "1px solid #D9DEE8", borderRadius: 8, padding: "0 10px", background: "#fff", fontSize: 13, color: "#364153" }}
                        aria-label="Select comparison country 1"
                    >
                        <option value="">Compare Country 1</option>
                        {comparisonFilterOptions.map((country) => (
                            <option key={country} value={country}>{country}</option>
                        ))}
                    </select>

                    <select
                        value={selectedComparisonCountryB}
                        onChange={(event) => setSelectedComparisonCountryB(event.target.value as ComparisonCountry)}
                        style={{ minWidth: 180, height: 36, border: "1px solid #D9DEE8", borderRadius: 8, padding: "0 10px", background: "#fff", fontSize: 13, color: "#364153" }}
                        aria-label="Select comparison country 2"
                    >
                        <option value="">Compare Country 2</option>
                        {comparisonFilterOptions.map((country) => (
                            <option key={country} value={country}>{country}</option>
                        ))}
                    </select>

                    <select
                        value={selectedComparisonCountryC}
                        onChange={(event) => setSelectedComparisonCountryC(event.target.value as ComparisonCountry)}
                        style={{ minWidth: 180, height: 36, border: "1px solid #D9DEE8", borderRadius: 8, padding: "0 10px", background: "#fff", fontSize: 13, color: "#364153" }}
                        aria-label="Select comparison country 3"
                    >
                        <option value="">Compare Country 3</option>
                        {comparisonFilterOptions.map((country) => (
                            <option key={country} value={country}>{country}</option>
                        ))}
                    </select>
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                        data={costComparisonData}
                        margin={{
                        top: 20,
                        right: 20,
                        left: 10,
                        bottom: 0,
                        }}
                    >
                        <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#E5E7EB"
                        />

                        <XAxis
                        dataKey="country"
                        tick={{
                            fontSize: 11,
                            fill: "#666",
                        }}
                        />

                        <YAxis
                        tickFormatter={(value) => value.toLocaleString()}
                        tick={{
                            fontSize: 11,
                            fill: "#666",
                        }}
                        />

                        <Tooltip
                        cursor={false}
                        formatter={(value: number, _name, props: any) => {
                            const currency = props?.payload?.currency || baseCurrencyCode;
                            return [formatCurrencyValue(value, currency), "Cost"];
                        }}
                        />

                        <Bar
                        dataKey="cost"
                        radius={[3, 3, 0, 0]}
                        barSize={120}
                        activeBar={false}
                        label={{
                            position: "top",
                            fill: "#666",
                            fontSize: 11,
                        }}
                        >
                        {costComparisonData.map((entry, index) => (
                            <Cell
                            key={index}
                            fill={entry.color}
                            />
                        ))}
                        </Bar>
                    </BarChart>
                    </ResponsiveContainer>
                    </>
                    </SectionCard>
                </div>
            </div>
            <div className="cma-footer">
                <SectionCard>
                    <ActionToolbar />
                </SectionCard>
            </div>
        </>
    )
}